import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

type FAQItem = { q: string; a: string };

export function FAQ() {
  const { t, i18n } = useTranslation();
  // Precios en vivo (los mismos que la sección de precios). Antes la FAQ los
  // tenía escritos a mano y se quedaron desfasados: Google llegó a mostrar
  // 34,90 € de Classic cuando costaba 39,40 €.
  const [precios, setPrecios] = useState<Record<string, number>>({ mini: 24.9, classic: 39.4, premium: 54.9, pareja: 74.9 });
  useEffect(() => {
    fetch('/api/site/prices').then(r => (r.ok ? r.json() : null)).then(d => {
      if (d && typeof d === 'object') setPrecios(p => ({ ...p, ...d }));
    }).catch(() => {});
  }, []);
  const punto = ['en', 'tl', 'sw'].includes(i18n.language);
  const fmt = (n: number) => { const s = Number(n).toFixed(2); return punto ? s : s.replace('.', ','); };
  const items = (t('faq.items', {
    returnObjects: true,
    mini: fmt(precios.mini), classic: fmt(precios.classic), premium: fmt(precios.premium), pareja: fmt(precios.pareja),
  }) as FAQItem[]) || [];
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  // FAQ Schema JSON-LD para Google
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(it => ({
      '@type': 'Question',
      name: it.q,
      acceptedAnswer: { '@type': 'Answer', text: it.a },
    })),
  };

  if (!items.length) return null;

  return (
    <section id="faq" className="py-20" style={{ backgroundColor: 'var(--page-bg)' }}>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="container mx-auto px-6 max-w-3xl">
        <div className="text-center mb-12">
          <div className="divider-ornament max-w-xs mx-auto mb-4">✦ ✦ ✦</div>
          <h2 className="text-3xl md:text-4xl font-semibold" style={{ color: 'var(--page-heading)' }}>
            {t('faq.title')}
          </h2>
          <p className="text-base text-[#4B5563] mt-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('faq.subtitle')}
          </p>
        </div>

        <div className="space-y-3">
          {items.map((it, i) => {
            const open = openIdx === i;
            return (
              <div
                key={i}
                className="rounded-xl border transition-all overflow-hidden"
                style={{
                  borderColor: open ? '#D4AF37' : '#E5E7EB',
                  backgroundColor: open ? '#FFF9E6' : '#FFFFFF',
                }}
              >
                <button
                  onClick={() => setOpenIdx(open ? null : i)}
                  className="w-full px-5 py-4 text-left flex items-center justify-between gap-4 hover:bg-amber-50/40 transition-colors"
                >
                  <span className="font-medium text-[#1E3A5F]">{it.q}</span>
                  <ChevronDown
                    className={`w-4 h-4 text-[#D4AF37] flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
                  />
                </button>
                {open && (
                  <div className="px-5 pb-4 text-sm text-[#374151] leading-relaxed">
                    {it.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
